import Card from '@/components/Card';
import CvContentPreview from '@/components/CvContentPreview.tsx';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { BuildDocument } from '@/components/placeholder/BuildDocument.tsx';
import { useProfileOnboarding } from '@/context/OnboarrdingContext';
import {
  DownloadOutlined,
  EditOutlined,
  ExpandOutlined,
  FileOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import {
  CVTemplateSelector,
  enLabels,
  frLabels,
  type TemplateViewer,
} from '@flemme-template/components';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Modal, notification, Segmented, Spin } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/core';
export const Route = createFileRoute('/build-documents')({
  component: RouteComponent,
});

const GENERATE_PDF_MUTATION = gql`
  mutation GeneratePdf($content: String!, $template: String!, $locale: String) {
    generatePdf(content: $content, template: $template, locale: $locale)
  }
`;
type Panel = 'left' | 'right';

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage;
  const labels = language === 'fr' ? frLabels : enLabels;
  const [panel, setPanel] = useState<Panel>('right');

  const { xs } = useBreakpoint();

  const [generatePdf, { loading: loadingGeneratePdf }] = useMutation(GENERATE_PDF_MUTATION);

  const navigate = useNavigate();
  const {
    profileResponse,
    isGenerating,
    contentRequest,
    cvGenerated,
    selectedTemplate,
    setSelectedTemplate,
  } = useProfileOnboarding();
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const mergedProfile = useMemo(() => {
    return {
      ...(profileResponse?.profile || {}),
      ...(cvGenerated?.cv || {}),
    };
  }, [profileResponse?.profile, cvGenerated?.cv]);

  function isIOSorIPad() {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Classic detection for iPhone, iPad, iPod
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

    // Newer iPads (iPadOS 13+) identify as "Macintosh" but have touch support
    const isIPadOS13Plus = userAgent.includes('Macintosh') && 'ontouchend' in document;

    return isIOSDevice || isIPadOS13Plus;
  }

  const handleDownload = async (template: TemplateViewer | null = null) => {
    if (!selectedTemplate) {
      notification.error({
        message: t('cv_content_preview.no_template_error'),
      });
      return;
    }

    try {
      const { data } = await generatePdf({
        variables: {
          content: JSON.stringify(mergedProfile),
          template: template?.id || selectedTemplate.id,
          locale: language,
        },
      });

      const trigger = () => {
        const a = document.createElement('a');
        a.href = data.generatePdf;
        a.target = '_blank';
        a.download = `${mergedProfile.name}-CV-${dayjs().format('YYYY-MM')}.pdf`;
        a.click();
      };
      if (data?.generatePdf) {
        if (isIOSorIPad()) {
          Modal.confirm({
            content: t('build-documents.ready'),
            okText: t('global.save_document'),
            cancelText: t('global.close'),
            onOk: trigger,
            type: 'success',
            icon: (
              <div className={'text-green-600 pr-2'}>
                <CheckCircleFilled />
              </div>
            ),
          });
        } else {
          trigger();
        }
      }
    } catch (error: any) {
      captureException(error);
      notification.error({
        key: 'error-notification',
        message: t('cv_content_preview.download_error'),
        actions: [
          <Button
            key="retry"
            type="default"
            onClick={() => {
              notification.destroy('error-notification');
              handleDownload(template);
            }}
          >
            {t('global.retry')}
          </Button>,
        ],
        // description: error.message,
      });
    }
  };

  console.log(mergedProfile);

  const [previewOpen, setPreviewOpen] = useState(false);

  const previewRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If no profile is present, redirect to edit-profile
    if (!profileResponse?.profile) {
      navigate({
        to: '/import-profile/edit-profile',
        search: { method: 'fill-from-zero' },
        replace: true,
      });
    }
  }, [profileResponse, navigate]);

  // If no profile, don't render anything
  if (!profileResponse?.profile) {
    return null;
  }

  const getProfileSummary = () => {
    const parts = [];
    if (profileResponse.profile.experiences?.length) {
      parts.push(
        `${profileResponse.profile.experiences.length} ${t('edit-profile.experience.short_title')}`
      );
    }
    if (profileResponse.profile.educations?.length) {
      parts.push(
        `${profileResponse.profile.educations.length} ${t('edit-profile.education.title')}`
      );
    }
    if (profileResponse.profile.certifications?.length) {
      parts.push(
        `${profileResponse.profile.certifications.length} ${t('edit-profile.certifications.title')}`
      );
    }
    if (profileResponse.profile.languages?.length) {
      parts.push(
        `${profileResponse.profile.languages.length} ${t('edit-profile.languages.title')}`
      );
    }
    return parts.slice(0, 2).join(', ').concat('...');
  };

  return (
    <main className="h-screen overflow-y-auto relative p-4 lg:pt-4 pt-0">
      <Spin spinning={loadingGeneratePdf} fullscreen />
      <div className={'sticky -top-4 z-20 bg-gray-50 py-4 block lg:hidden'}>
        <Segmented<Panel>
          className="ant-segmented-full-width lg:!hidden"
          options={[
            {
              label: (
                <div className="flex items-center gap-2">
                  <SearchOutlined />
                  <span>{t('build-documents.left')}</span>
                </div>
              ),
              value: 'left',
            },
            {
              label: (
                <div className="flex items-center gap-2">
                  <FileOutlined />
                  <span>{t('build-documents.right')}</span>
                </div>
              ),
              value: 'right',
            },
          ]}
          onChange={value => {
            setPanel(value);
          }}
          value={panel}
        />
      </div>
      <div className={'mx-auto max-w-[1150px] flex items-stretch gap-5 min-h-screen'}>
        {/* Left Sidebar */}
        <div className={clsx('lg:!w-[340px] w-full', panel === 'right' && 'hidden lg:block')}>
          <div className={'flex flex-col gap-5 sticky lg:top-8'}>
            <LanguageSwitcher />
            <Card
              title={profileResponse.profile.name}
              description={getProfileSummary()}
              icon={<EditOutlined />}
              onClick={() =>
                navigate({
                  to: '/import-profile/edit-profile',
                  search: { method: undefined },
                })
              }
            />

            <Card
              title={t('build-documents.job_info')}
              description={contentRequest?.jobDescription || ''}
              maxDescriptionLength={215}
              actionBottom
              icon={<EditOutlined />}
              onClick={() =>
                navigate({
                  to: '/get-offer-details',
                })
              }
            />

            {selectedTemplate ? (
              <div className="bg-white rounded-2xl shadow-card border border-solid border-transparent">
                <div className="h-[200px] rounded-t-2xl overflow-hidden bg-gray-50">
                  <div style={{ transform: 'scale(0.2)', transformOrigin: '0 0', width: '500%' }}>
                    <selectedTemplate.Component
                      // @ts-expect-error some property
                      profile={mergedProfile}
                      labels={labels}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Button
                      type="link"
                      icon={<ExpandOutlined />}
                      onClick={() => setPreviewOpen(true)}
                    >
                      {t('build-documents.template.preview')}
                    </Button>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => setShowTemplateSelector(true)}
                    >
                      {t('build-documents.template.change')}
                    </Button>
                  </div>
                  <h3 className="subtitle-1">{t('build-documents.save_as_file')}</h3>
                  <p className="body-2 text-gray">{t('build-documents.download_description')}</p>
                  <div className="flex flex-col gap-2 pt-4">
                    <Button
                      type="primary"
                      size="large"
                      loading={loadingGeneratePdf}
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload()}
                    >
                      {t('build-documents.download_pdf')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Card
                title={t('build-documents.download_pdf')}
                description={t('build-documents.download_description')}
                maxDescriptionLength={215}
                actionBottom
                footer={
                  <Button
                    type="primary"
                    onClick={() => setShowTemplateSelector(true)}
                    size="large"
                    className="w-full"
                    icon={<DownloadOutlined />}
                  >
                    {t('build-documents.select_template')}
                  </Button>
                }
              />
            )}
          </div>
        </div>
        <div className={clsx('flex-1 max-w-full', panel === 'left' && 'hidden lg:block')}>
          {/* Right Content */}
          {isGenerating || !cvGenerated ? (
            <BuildDocument />
          ) : (
            <CvContentPreview
              onDownload={() => {
                if (!selectedTemplate) {
                  notification.info({
                    message: t('build-documents.download_description'),
                  });
                  setShowTemplateSelector(true);
                } else {
                  handleDownload();
                }
              }}
              content={cvGenerated}
              profile={profileResponse}
            />
          )}
        </div>
      </div>

      <Modal
        open={showTemplateSelector}
        onCancel={() => setShowTemplateSelector(false)}
        footer={null}
        width="100%"
        title={t('index.title')}
        style={{ top: 40, padding: 0 }}
        classNames={{
          body: 'bg-gray-100 lg:rounded-2xl',
        }}
      >
        <div className="lg:p-8 p-0 max-h-[calc(100vh-150px)] overflow-y-auto">
          <CVTemplateSelector
            shareLabel={t('global.choose')}
            // @ts-expect-error some property
            profile={mergedProfile}
            onTemplateSelected={template => {
              setSelectedTemplate(template);

              if (xs) {
                setShowTemplateSelector(false);
                handleDownload(template);
                return;
              }

              setShowTemplateSelector(false);
              setPreviewOpen(true);
              setTimeout(() => {
                if (previewRootRef.current) {
                  previewRootRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 100);
            }}
          />
        </div>
      </Modal>

      {selectedTemplate && (
        <Modal
          open={previewOpen}
          onCancel={() => setPreviewOpen(false)}
          width="100%"
          classNames={{
            body: 'bg-gray-100 rounded-2xl',
          }}
          footer={[
            <Button
              key="change"
              type="default"
              icon={<EditOutlined />}
              disabled={loadingGeneratePdf}
              onClick={() => {
                setShowTemplateSelector(true);
                setPreviewOpen(false);
              }}
            >
              {t('build-documents.template.change')}
            </Button>,
            <Button
              key="download"
              loading={loadingGeneratePdf}
              icon={<DownloadOutlined />}
              type="primary"
              onClick={() => handleDownload()}
            >
              {t('build-documents.download_pdf')}
            </Button>,
          ]}
          style={{ top: 40, padding: 0 }}
        >
          <div
            ref={previewRootRef}
            className="rounded-2xl max-h-[calc(100vh-160px)] overflow-y-auto mt-6 max-w-[1224px] mx-auto p-4"
          >
            <selectedTemplate.Component
              // @ts-expect-error some property
              profile={mergedProfile}
              labels={labels}
            />
          </div>
        </Modal>
      )}
    </main>
  );
}
